import { Link } from '@tanstack/react-router';
import { ChevronRight, FileText, Image as ImageIcon } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';

import { MeterBar } from '../../../chrome';
import { HintedIcon, IconHint } from '../../../chrome/hint';
import { EmptyState } from '../../../chrome/state';
import { DataTable, type ColumnDef } from '../../../chrome/table';
import type { PostRow } from '../api/schemas';
import {
  DeletedBadge,
  FigureValue,
  KindBadge,
  Metric,
  PostAuthor,
  PostCaption,
  PostMeta,
  SentimentChip,
  StoryCaptured,
  StoryExpiry,
} from './cells';
import { pub } from './messages';
import { isStory, reachOf, type PostEntry } from './readings';

/** The row's media-kind glyph, not the post's own thumbnail: a fixed tile so the
 *  column never reflows around an image that loads late or fails. */
function MediaKindTile(props: { post: PostRow }): ReactNode {
  const hasMedia = props.post.media.length > 0;
  const Icon = hasMedia ? ImageIcon : FileText;
  return (
    <HintedIcon
      label={hasMedia ? pub('list.mediaTile') : pub('list.textTile')}
      className="bg-secondary text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-md"
    >
      <Icon aria-hidden className="size-4" />
    </HintedIcon>
  );
}

function columnsFor(
  maxReach: number,
  hrefFor?: (entry: PostEntry) => string,
): ColumnDef<PostEntry, unknown>[] {
  return [
    {
      id: 'post',
      header: () => pub('list.post'),
      cell: ({ row }) => {
        const { post } = row.original;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <MediaKindTile post={post} />
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-body-sm truncate">
                <PostCaption post={post} />
              </span>
              <span className="text-caption text-muted-foreground flex min-w-0 items-center gap-2">
                <PostAuthor post={post} />
                <PostMeta post={post} />
                <KindBadge post={post} />
                <DeletedBadge post={post} />
              </span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'reach',
      header: () => pub('list.reach'),
      meta: { numeric: true },
      cell: ({ row }) =>
        isStory(row.original.post) ? null : (
          <div className="ms-auto flex w-36 flex-col items-end gap-1.5">
            <span className="text-label">
              <FigureValue figures={row.original.figures} metric="reach" />
            </span>
            <div className="w-full">
              <MeterBar value={reachOf(row.original)} max={maxReach} label={pub('metric.reach')} />
            </div>
          </div>
        ),
    },
    {
      id: 'engagement',
      header: () => pub('list.engagement'),
      cell: ({ row }) => {
        const { post, figures } = row.original;
        if (isStory(post)) {
          return (
            <div className="text-caption text-muted-foreground flex flex-col items-start gap-1">
              {post.captured_at !== null && <StoryCaptured capturedAt={post.captured_at} />}
              {post.expires_at !== null && <StoryExpiry expiresAt={post.expires_at} />}
            </div>
          );
        }
        return (
          <div className="flex items-center gap-4">
            <Metric figures={figures} metric="likes" />
            <Metric figures={figures} metric="comments" />
            <Metric figures={figures} metric="shares" />
            <Metric figures={figures} metric="saves" />
          </div>
        );
      },
    },
    {
      id: 'sentiment',
      header: () => pub('list.sentiment'),
      meta: { numeric: true },
      cell: ({ row }) =>
        isStory(row.original.post) ? null : <SentimentChip sentiment={row.original.sentiment} />,
    },
    {
      id: 'chevron',
      header: () => null,
      cell: ({ row }) => {
        const chevron = <ChevronRight aria-hidden className="size-4" />;
        if (hrefFor === undefined) return <span className="text-muted-foreground">{chevron}</span>;
        return (
          <IconHint label={pub('list.open')}>
            <Link
              to={hrefFor(row.original)}
              className="text-muted-foreground flex items-center justify-end focus-visible:underline"
            >
              {chevron}
            </Link>
          </IconHint>
        );
      },
    },
  ];
}

export function PostList(props: {
  entries: PostEntry[];
  loading?: boolean;
  hrefFor?: (entry: PostEntry) => string;
}): ReactNode {
  // Bars are relative to the largest reach on screen, and never divide by zero.
  const maxReach = Math.max(
    1,
    ...props.entries.flatMap((entry) => {
      const reach = reachOf(entry);
      return reach === null ? [] : [reach];
    }),
  );
  const { hrefFor } = props;
  const columns = useMemo(() => columnsFor(maxReach, hrefFor), [maxReach, hrefFor]);
  return (
    <DataTable
      columns={columns}
      data={props.entries}
      loading={props.loading}
      caption={pub('list.caption')}
      getRowId={(entry) => entry.post.post_id}
      emptyState={<EmptyState title={pub('empty.title')} />}
    />
  );
}
